import { useMutation, useQuery } from '@apollo/client';
import {
  GQL_ACCEPT_CHALLENGE,
  GQL_DECLINE_CHALLENGE,
  GQL_GET_PENDING_BATTLES,
  GQL_GET_SENT_BATTLES,
  StoredsentBattleSelector,
  StoredPendingBattle,
} from '~/gql/battles/battles';
import { authClient } from '~/helpers/apollo';
import iconBattle from '@assets/images/background/mode-battle.svg';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useLingui } from '@lingui/react';
import { useState } from 'react';
import { WonBattleItem } from '~/components/ui/won-battle-item';
import { formatDate, renderFullPath } from '~/utils/common/utils';
import { SSpanTableAdvice, SSpanTableName } from '~/components/styles/styles';
import ButtonSmall from '~/components/ui/button-small';
import ModalListAuto from '~/components/ui/modal-list-auto';
import { WrapperListAuto } from '~/components/ui/wrapper-list-auto';
import { classic } from '~/themes';
import { BattleStatus } from '@zeus/index';
import { useAuth } from '~/helpers/store/auth';

const BattleRequest = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { payload } = useAuth();
  const client = authClient;
  const [pendingBattles, setPendingBattles] = useState<StoredPendingBattle[]>(
    []
  );
  const [sentBattle, setSentBattle] = useState<StoredsentBattleSelector[]>([]);
  const [tab, setTab] = useState<'current' | 'history'>('current');

  const { refetch } = useQuery(GQL_GET_PENDING_BATTLES, {
    client,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    variables: {
      isChallenger: false,
    },
    onCompleted: (data) => {
      const sortedPendingBattles = [...data.getPendingBattles].sort(
        compareByDate
      );

      setPendingBattles(sortedPendingBattles);
    },
  });

  function compareByDate(
    a: StoredPendingBattle,
    b: StoredPendingBattle
  ): number {
    return (
      new Date(a.ttl as Date).getTime() - new Date(b.ttl as Date).getTime()
    );
  }

  const { refetch: refetchSentBattles } = useQuery(GQL_GET_SENT_BATTLES, {
    client: authClient,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setSentBattle(data.getSentBattles);
    },
  });

  const [answerRequest] = useMutation(GQL_ACCEPT_CHALLENGE, {
    client,
    onCompleted: (data) => {
      navigate(
        `/mode-battle/invitations/${data.acceptChallenge.battlePlayerTrackingId}`
      );
    },
  });

  const [declineChallenge] = useMutation(GQL_DECLINE_CHALLENGE, {
    client,
    onCompleted: () => {
      refetch();
      refetchSentBattles();
    },
  });

  function getStatusName(status: BattleStatus): string {
    if (status === BattleStatus.DECLINED) return 'décliné';
    if (status === BattleStatus.FINISHED) return 'fini';
    if (status === BattleStatus.IN_PROGRESS) return 'en attente';
    if (status === BattleStatus.LOST) return 'perdu';
    if (status === BattleStatus.NOT_STARTED) return 'en attente';
    if (status === BattleStatus.PENDING) return 'en attente';
    if (status === BattleStatus.WON) return 'gagné';
    return '';
  }

  function formatFinishDate(targetDate: Date): string {
    const now = new Date();

    const diffMs = new Date(targetDate).getTime() - now.getTime();

    if (diffMs <= 0) {
      return ''; // date incorrecte
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays >= 1) {
      return `J-${diffDays}`;
    } else if (diffHours >= 1) {
      return `H-${diffHours}`;
    } else {
      return `M-${diffMinutes}`;
    }
  }

  return (
    <ModalListAuto
      iconSrc={iconBattle}
      open={true}
      onClose={() => navigate('/mode-battle')}
    >
      <WrapperListAuto
        onClose={() => navigate('/mode-battle')}
        title={`${i18n._('battle.my.invitations')}`}
        titleWithoutBorder
      >
        <Container>
          <SDivTab>
            <STabItem
              onClick={() => setTab('current')}
              $selected={tab === 'current'}
            >
              <SSpanTab $selected={tab === 'current'}>
                {i18n._('challenge.current')}
              </SSpanTab>{' '}
            </STabItem>
            <STabItem
              onClick={() => setTab('history')}
              $selected={tab === 'history'}
            >
              <SSpanTab $selected={tab === 'history'}>
                {i18n._('challenge.history')}
              </SSpanTab>{' '}
            </STabItem>
          </SDivTab>
          <STableWrapper>
            {tab === 'current' && (
              <STable>
                <tbody>
                  {pendingBattles.map((battle, indBattle) => (
                    <tr key={indBattle}>
                      <STdReceived>
                        <WonBattleItem
                          wonBattles={battle.challenger.wonBattles}
                        />
                      </STdReceived>
                      <STdReceived>
                        {battle.challenger.avatar?.fileName && (
                          <SDivAvatar>
                            <img
                              src={renderFullPath(
                                battle.challenger.avatar?.fileName
                              )}
                              alt=""
                            />
                          </SDivAvatar>
                        )}
                      </STdReceived>
                      <STdReceived>
                        <SDivName>
                          <SSpanTableName>
                            {battle.challenger.username}
                          </SSpanTableName>
                          <SSpanTableAdvice className="hidden">
                            {`Envoyé le ${formatDate(battle.createdAt as Date)}`}
                          </SSpanTableAdvice>
                        </SDivName>
                      </STdReceived>
                      <STdReceived>
                        <SDivBtn>
                          {battle.currentPlayerId === payload?.uuid && (
                            <>
                              {battle.challenger.battleStatus ===
                                BattleStatus.IN_PROGRESS && (
                                <ButtonSmall
                                  color="secondary"
                                  onClick={() =>
                                    answerRequest({
                                      variables: { battleId: battle.id },
                                    })
                                  }
                                >
                                  {i18n._('battle.replay')}
                                  {' ('}
                                  {formatFinishDate(battle.ttl as Date)}
                                  {')'}
                                </ButtonSmall>
                              )}
                              {battle.challenger.battleStatus ===
                                BattleStatus.PENDING && (
                                <>
                                  <ButtonSmall
                                    color="primary"
                                    onClick={() =>
                                      answerRequest({
                                        variables: { battleId: battle.id },
                                      })
                                    }
                                  >
                                    {i18n._('battle.accept')}
                                    {' ('}
                                    {formatFinishDate(battle.ttl as Date)}
                                    {')'}
                                  </ButtonSmall>
                                  <ButtonSmall
                                    color="grey"
                                    onClick={() =>
                                      declineChallenge({
                                        variables: { battleId: battle.id },
                                      })
                                    }
                                  >
                                    {i18n._('battle.cancel')}
                                  </ButtonSmall>
                                </>
                              )}
                            </>
                          )}

                          {battle.currentPlayerId !== payload?.uuid && (
                            <>
                              <SSpan $status={battle.challenger.battleStatus}>
                                {'En attente'}
                                {' ('}
                                {formatFinishDate(battle.ttl as Date)}
                                {')'}
                              </SSpan>
                            </>
                          )}
                        </SDivBtn>
                      </STdReceived>
                    </tr>
                  ))}
                </tbody>
              </STable>
            )}

            {tab === 'history' && (
              <STable>
                <tbody>
                  {sentBattle.map((battle, indBattle) => (
                    <tr key={indBattle}>
                      <STdSent>
                        <SDivNameWrapper>
                          {battle.avatar?.fileName && (
                            <SDivAvatar>
                              <img
                                src={renderFullPath(battle.avatar?.fileName)}
                                alt=""
                              />
                            </SDivAvatar>
                          )}
                          <SDivName>
                            <SSpanTableName>{battle.username}</SSpanTableName>
                            <SSpanTableAdvice>
                              {`envoyé le ${formatDate(battle.createdAt as Date)}`}
                            </SSpanTableAdvice>
                          </SDivName>
                        </SDivNameWrapper>
                      </STdSent>
                      <STdSent>
                        <SDivStatus>
                          <SSpan $status={battle.battleStatus}>
                            {getStatusName(battle.battleStatus)}
                          </SSpan>
                        </SDivStatus>
                      </STdSent>
                    </tr>
                  ))}
                </tbody>
              </STable>
            )}
          </STableWrapper>
        </Container>
      </WrapperListAuto>
    </ModalListAuto>
  );
};

export default BattleRequest;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const STdSent = styled.td`
  &:first-of-type {
    text-align: center;
    width: 60%;
    user-select: none;
  }

  &:last-of-type {
    padding-right: 10px;
    text-align: right;
    width: 40%;
    user-select: none;
  }
`;

const STdReceived = styled.td`
  &:first-of-type {
    text-align: center;
    width: 8%;
    user-select: none;
  }
  &:nth-of-type(2) {
    width: 8%;
    user-select: none;
  }
  &:nth-of-type(3) {
    width: 34%;
    user-select: none;
  }
  &:last-of-type {
    padding-right: 10px;
    text-align: right;
    width: 30%;
    user-select: none;
  }
`;

const SSpan = styled.span<{
  $color?: string;
  $align?: string;
  $status: string;
}>`
  color: ${({ $color, $status }) => {
    if ($status === 'DECLINED') return `${classic.token?.colorError}`;
    if ($status === BattleStatus.WON) return `${classic.token?.colorSuccess}`;
    if (
      $status === 'PENDING' ||
      $status === 'IN_PROGRESS' ||
      $status === 'NOT_STARTED'
    )
      return `${classic.token?.colorTextSecondary}`;
    if ($status === BattleStatus.LOST) return `${classic.token?.colorError}`;
    if ($status === BattleStatus.FINISHED) return `${classic.token?.colorText}`;
    return $color ? $color : `${classic.token?.colorTextSecondary}`;
  }};
  font-family: 'Riffic Free', sans-serif;
  font-weight: 700;
  font-size: 1.1rem;
  text-transform: uppercase;

  text-align: ${({ $align }) => ($align ? $align : 'left')};
  user-select: none;
  @media screen and (max-height: 600px) {
    font-size: 1rem;
  }
  @media screen and (max-height: 450px) {
    margin-bottom: 10px;
    font-size: 0.9rem;
  }
  @media screen and (max-width: 450px) {
    font-size: 0.9rem;
  }
`;

const SDivStatus = styled.div<{ $isWinner?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: center;
  border-radius: 12.8px;

  padding: 2px 8px;
  min-width: 100px;
  height: auto;
`;

const SDivNameWrapper = styled.div`
  background-color: transparent;
  height: 46px;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SSpanTab = styled.h2<{ $selected: boolean; $marginBottom?: string }>`
  color: ${({ $selected }) =>
    $selected ? `${classic.token?.colorText}` : `#FFFFFF80`};
  font-family: 'Riffic Free', sans-serif;
  font-weight: 700;
  font-size: 1.3rem;
  text-align: center;
  user-select: none;

  @media screen and (max-height: 450px) {
    font-size: 1rem;
  }
  @media screen and (max-width: 450px) {
    font-size: 1rem;
  }
`;

const SDivTab = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;

  width: 100%;
`;

const STabItem = styled.button<{ $selected: boolean }>`
  align-items: center;
  cursor: pointer;
  justify-content: center;
  border: none;
  background: ${({ $selected }) =>
    $selected
      ? 'none'
      : 'linear-gradient(0deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.40) 100%), var(--Primary, #53D2D7)'};
  display: flex;
  height: 44px;
  width: 50%;
  outline: none;
`;

const SDivName = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  margin-left: 10px;
  height: 100%;
  width: 100%;
`;

const SDivAvatar = styled.div`
  border-radius: 50%;
  background-color: #fff;
  height: 40px;
  width: 40px;
  position: relative;
  border: 3px solid #fff;

  img {
    position: absolute;
    object-fit: contain;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
  }
`;

const SDivBtn = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 40px;
  width: 100%;
`;

const STableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  overflow-y: auto;
  padding: 20px 20px 130px 20px;

  @media screen and (max-height: 800px) {
    padding: 10px 10px 130px 40px;
  }

  @media screen and (max-height: 450px) {
    padding: 10px 10px 100px 40px;
  }

  @media screen and (max-width: 450px) {
    padding: 10px 10px 100px 10px;
  }
`;

const STable = styled.table`
  text-align: left;
  position: relative;
  border-collapse: collapse;
  width: 100%;

  tbody {
    tr {
      border-bottom: 1px solid rgba(0, 0, 0, 0.2);
      &:nth-of-type(even) {
        user-select: none;
      }
      &:nth-of-type(odd) {
        background-color: transparent;
        user-select: none;
      }
      td {
        color: #113969;
        font-family: 'Riffic Free', sans-serif;
        background-color: transparent;
        font-weight: bold;
        font-size: 1.1rem;
        padding: 8px 12px;
        user-select: none;

        @media screen and (max-width: 450px) {
          font-size: 0.8rem;
          padding: 5px 8px;
        }
        @media screen and (max-width: 290px) {
          font-size: 0.7rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 600px) {
          font-size: 0.9rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 450px) {
          font-size: 0.8rem;
          padding: 5px 8px;
        }
        @media screen and (max-height: 290px) {
          font-size: 0.7rem;
          padding: 5px 8px;
        }
      }

      & > td > div > span {
        transition: all 0.5s linear;
        &.hidden {
          visibility: hidden;
          height: 0;
          opacity: 0;
        }
      }

      &:hover {
        & > td > div > span {
          &.hidden {
            visibility: visible;
            height: auto;
            opacity: 1;
          }
        }
      }
    }
  }
`;
